import { getIdToken } from '@/services/auth';
import { paths } from '@/types/api';
import { fetchClient } from '@/util/fetchClient';

type CompleteOAuthSignupPath = paths['/v2/auth/complete-signup']['post'];

export type CompleteOAuthSignupRequest =
  CompleteOAuthSignupPath['requestBody']['content']['application/json'];

export type CompleteOAuthSignupResponse =
  CompleteOAuthSignupPath['responses']['201']['content']['application/json'];

export type CompleteOAuthSignupReferralExtras = {
  referredById?: number;
  referredByPrefix?: string;
  referredByName?: string;
};

export type CompleteOAuthSignupResult = CompleteOAuthSignupResponse['data'] &
  CompleteOAuthSignupReferralExtras;

/**
 * Complete OAuth signup after NPI verification.
 * Requires OAuth ID token in Authorization header even for pending users.
 *
 * Creates DB user + Twilio user + sends welcome email after user provides NPI.
 * Used for Google OAuth flow where user creation is deferred until NPI verification.
 * Middleware throws on non-2xx responses; reaching the success path guarantees data exists.
 */
export async function completeOAuthSignup(
  data: CompleteOAuthSignupRequest
): Promise<CompleteOAuthSignupResult> {
  const idToken = await getIdToken();

  if (!idToken) {
    throw new Error('Missing OAuth ID token');
  }

  const response = await fetchClient.POST('/v2/auth/complete-signup', {
    body: data,
    headers: { Authorization: `Bearer ${idToken}` },
  });

  const { data: resData } = response;

  if (!resData || !resData.data) {
    throw new Error('Unexpected empty response');
  }

  const referral = resData.data?.referrer
    ? {
        referredById: resData.data.referrer.id,
        referredByPrefix: 'Referred by',
        referredByName:
          [resData.data.referrer.firstName, resData.data.referrer.lastName]
            .filter(Boolean)
            .join(' ') || undefined,
      }
    : {};

  return {
    ...resData.data,
    ...referral,
  } as CompleteOAuthSignupResult;
}
